import type { Row5 } from "@cloudnest/yinsh-engine";

/**
 * Resolving which 5-run to score when 5-rows overlap (design decision
 * 2026-05-22). Each engine `Row5` is an ordered run; its two ends are
 * `positions[0]` and `positions[4]`. The player taps the two extreme markers
 * of the run they want, so a run matches when its endpoints equal the picked
 * {begin, end} pair — in either tap order.
 *
 * An endpoint pair is unique across all pending rows: two cells can be the
 * extremes of a length-5 line only if they are exactly four steps apart on a
 * single lattice axis, and a given pair is four-apart on at most one axis — so
 * at most one straight 5-run spans any {begin, end}. First match is therefore
 * the only match; no ambiguity even when runs overlap on interior markers.
 *
 * Returns the matching run's positions, or null when no candidate run has
 * those two endpoints (an invalid pick the caller can reset).
 */
export function resolveRowRun(
  pendingRows: readonly Row5[],
  begin: string,
  end: string,
): Row5["positions"] | null {
  if (begin === end) return null;
  for (const row of pendingRows) {
    const first = row.positions[0];
    const last = row.positions[row.positions.length - 1];
    if (
      (first === begin && last === end) ||
      (first === end && last === begin)
    ) {
      return row.positions;
    }
  }
  return null;
}

/** The two-tap pick state: begin chosen, then end. */
export interface RowPick {
  readonly begin: string | null;
  readonly end: string | null;
}

export const EMPTY_ROW_PICK: RowPick = { begin: null, end: null };

export interface RowPickResult {
  readonly pick: RowPick;
  /** Non-null once both endpoints resolve to a valid run; commit then reset. */
  readonly run: Row5["positions"] | null;
}

/**
 * Advance the begin/end pick when a candidate marker is tapped. Pure so the
 * picker's tap sequencing is unit-testable.
 *
 * - first tap sets `begin`;
 * - re-tapping `begin` clears it (toggle off);
 * - a second, different tap tries to resolve a run: if it matches a candidate
 *   it's returned as `run`; otherwise the new tap becomes the fresh `begin`
 *   (so the player can re-aim without an explicit reset).
 */
export function applyRowPick(
  current: RowPick,
  pendingRows: readonly Row5[],
  tapped: string,
): RowPickResult {
  if (current.begin === null) {
    return { pick: { begin: tapped, end: null }, run: null };
  }
  if (current.begin === tapped) {
    return { pick: EMPTY_ROW_PICK, run: null };
  }
  const run = resolveRowRun(pendingRows, current.begin, tapped);
  if (run) {
    return { pick: { begin: current.begin, end: tapped }, run };
  }
  return { pick: { begin: tapped, end: null }, run: null };
}
